import Date from '@/shared/filters/Date'
import { API, APIObject } from '@/shared/plugins/Api/API'
import Store from '@/shared/store'
import _cloneDeep from 'lodash/cloneDeep'
import _get from 'lodash/get'
import _omit from 'lodash/omit'

let zIndexCount = 10000

class LogicalObject extends APIObject {
  constructor (options, fromLakehouse) {
    // Init
    super('DM', options)

    // Default variables
    this.display_name = this.display_name || this.name || ''
    this.description = this.description || ''
    this.tags = this.tags || {}
    this.tags.path = this.tags.path || ''
    this.tags.tags = this.tags.tags || []
    if (!this.tags.zIndex || isNaN(this.tags.zIndex)) {
      this.tags.zIndex = zIndexCount
      zIndexCount++
    }
    if (!Array.isArray(this.tags.tags)) this.tags.tags = []
    this.attributes = this.attributes || []
    this.name = this.name || ''
    this.type = this.type || 'prim'
    this.display = this.display || {}
    this.display.x = this.display.x || 0
    this.display.y = this.display.y || 0
    this.policy_tags = this.policy_tags || {}
    this.indexes = this.indexes || []
    this.max_snapshot_age = this.max_snapshot_age || null
    this.required_fields = this.required_fields || []

    // Status of physical build
    // PROCESSING = "PROCESSING"
    // SUCCESS = "SUCCESS"
    // FAILED = "FAILED"
    // STOPPED = "STOPPED"
    // TIMEOUT = "TIMEOUT"
    // From frontend we add also "NOT_BUILD" and "STARTED"
    this.build_status = this.build_status || 'NOT_BUILD'

    // For endpoints
    this.DM_BASE_URL = 'v4/logical/objects'
  }

  get buildStatus () {
    return this.build_status || 'NOT_BUILD'
  }

  set buildStatus (value) {
    this.build_status = value
  }

  getX (viewId = null) {
    return _get(this, `display.views.${viewId}.x`, this.display.x)
  }

  getY (viewId = null) {
    return _get(this, `display.views.${viewId}.y`, this.display.y)
  }

  assign (object) {
    super.assign(object)
    this.created_at = Date(this.created_at) || null
    this.updated_at = Date(this.updated_at) || null
  }


  async physicalBuild () {
    await this.request({
      method: 'put',
      socketId: false,
      url: 'v4/build/start',
      data: {
        objects: [this._id]
      }
    })
  }


  clone () {
    this.table_origin_id = this._id
    return new LogicalObject(_cloneDeep(this._filter(this)))
  }

  _filter (object) {
    const obj = _omit(super._filter(object), [
      '_id',
      '__socketId',
      'created_at',
      'updated_at',
      'created_by',
      'build_status',
      'buildStatus',
      'updated_by',
      'children',
      'DM_BASE_URL'
    ])

    return obj
  }

  async create (type = 'empty', fromElement = '') {
    this.display_name = this.display_name || this.name

    this.templated_from = type === 'empty' ? 'json' : type === 'from-file' ? 'file' : 'connectors'
    if (type === 'from-file') this.template_filepath = fromElement
    if (type === 'connectors') this.template_catalog_object = fromElement

    const options = {
      method: 'POST',
      url: this.DM_BASE_URL
    }

    const newLogicalObject = await super.create(options)

    // We do this because if we duplicate logical object, it will recreate attributes 2 times
    if (type !== 'empty') {
      delete this.templated_from
      delete this.template_filepath
      delete this.template_catalog_object
    }

    return newLogicalObject
  }

  async duplicate () {
    this.templated_from = 'json'
    const request = this.request({
      method: 'POST',
      url: `${this.DM_BASE_URL}/copy`,
      data: this.toJSON()
    })

    // Remove unnecessary properties to prevent double creation of attributes afterwards
    delete this.templated_from
    delete this.template_filepath
    delete this.template_catalog_object

    return request
  }

  async save () {
    this.display_name = this.display_name || this.name
    return super.save({
      method: 'PUT',
      url: `${this.DM_BASE_URL}/${this._id}`
    })
  }

  async remove () {
    return this.request({
      method: 'DELETE',
      url: `${this.DM_BASE_URL}/${this._id}`
    })
  }

  async move (x, y, viewId = null) {
    const tableViews = _cloneDeep(this.display)
    const dwhTabs = (Store.getters.DWH_TABS || []).map(view => view._id)
    tableViews.views = tableViews.views || {}

    // If view was deleted, we removed it from display of table
    Object.keys(tableViews.views).forEach(view => {
      if (!dwhTabs.includes(view)) delete tableViews.views[view]
    })

    // Set position of table in each views
    dwhTabs.forEach(id => {
      if (!tableViews.views[id]) {
        tableViews.views[id] = {
          x: this.display.x,
          y: this.display.y
        }
      }
    })

    // If no viewID => update position in all tables else update position in views
    if (!viewId) {
      tableViews.x = x
      tableViews.y = y
    } else {
      tableViews.views[viewId] = { x, y }
    }

    this.update('display', tableViews, false)
  }
}
class LogicalObjects extends API {
  new (opts) {
    return new LogicalObject(opts)
  }

  list () {
    const url = 'v4/logical/objects'
    return this.request({
      method: 'get',
      url,
      params: {
        attribute_format: 'id',
        policy_tags: true
      }
    })
      .then(objects => {
        // objects = require('./mock_tables.json')

        return objects.map(object => {
          try {
            return new LogicalObject(object)
          } catch (err) {
            console.error(err.stack)
            return null
          }
        }).filter(i => i)
      })
  }
}

export default LogicalObjects
export {
  LogicalObject,
  LogicalObjects
}
